import { useState, useRef, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../PageTitle/PageTitle";
import DefaultButton from "../../components/simple/DefaultButton";
import DragDropFileUpload from "../../components/DragDropFileUpload/DragDropFileUpload";
import { Alert } from "@mui/material";

import Form from "../Form/Form";
import Input from "../Form/Input/Input";

import { useAuth } from "../../contexts/AuthContext";
import { BrandContext } from "../../App";
import { useAPI } from "/src/hooks/useAPI";

import { ConvoInsightsContainer } from "./style.js";

import Modal from "../Modal/Modal";
import StackedProgress from "../StackedProgress/StackedProgress";
import CircularProgress from "@mui/material/CircularProgress";

import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const TitleWatcher = ({ watch, setTranscriptData }) => {
  const watchedTitle = watch("conversation_insight_name");
  useEffect(() => {
    if (watchedTitle) {
      setTranscriptData((prev) => ({
        ...prev,
        name: watchedTitle,
      }));
    }
  }, [watchedTitle, setTranscriptData]);
  return null;
};

const ProgramWatcher = ({
  setValue,
  options,
  setTranscriptData,
  currentProgramId,
}) => {
  useEffect(() => {
    // If we have options available and no program is currently selected
    if (options && options.length > 0 && !currentProgramId) {
      const firstOptionValue = options[0].value;

      // Update react-hook-form UI state
      setValue("program_filter", firstOptionValue);

      // Update local transcriptData state
      setTranscriptData((prev) => ({
        ...prev,
        training_program_id: firstOptionValue,
      }));
    }
  }, [options, currentProgramId, setValue, setTranscriptData]);

  return null;
};

const ConversationInsights = () => {
  const { authToken } = useAuth();
  const { brand, allTrainingPrograms } = useContext(BrandContext);

  const [isLoading, setIsLoading] = useState(false);
  const [generating, setIsGenerating] = useState(false);
  const [transcriptData, setTranscriptData] = useState({
    file: "",
    name: "",
    subject: "",
    training_program_id: "",
    availableSubjects: [],
    generationProgress: {},
    id: "",
  });

  const [modal, setModal] = useState({ type: "", show: false, title: "" });
  const [isValidFile, setIsValidFile] = useState(false);
  const [message, setMessage] = useState({ type: "", msg: "" }); // error or success

  const { callAPI } = useAPI();

  const navigate = useNavigate();

  const whatNext = [
    "Analyse your transcript",
    "Extract Key Speakers",
    "Identify speakers",
    "Prepare Personalised Feedback",
  ];

  // this could be converted to callAPI
  // as we now can use FormData in the body
  const handleFilesSelected = (
    files,
    setUploadProgress,
    setError,
    setSuccess,
  ) => {
    setIsLoading(true);
    files.forEach((file, index) => {
      const formData = new FormData();
      formData.append(`transcript`, file);

      setTranscriptData((prev) => ({
        ...prev,
        name: file.name,
        file: file,
      }));

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) =>
            prev.map((item, i) => (i === index ? { ...item, progress } : item)),
          );
        }
      };

      xhr.onload = async () => {
        try {
          // Parse the JSON regardless of the HTTP status code
          const response = JSON.parse(xhr.response);

          if (xhr.status >= 200 && xhr.status < 300) {
            // Some APIs return 200 OK but with valid: false. Handle that just in case.
            if (response.valid === false) {
              setSuccess(false);
              setIsValidFile(false);
              const errorMessage =
                response.error?.transcript?.[0] || "Invalid file.";
              setError(errorMessage);
            } else {
              setSuccess(
                "Transcript validated successfully, click Generate Feedback to continue",
              );
              setIsValidFile(true);
              setError(null);
              setTranscriptData((prev) => ({
                ...prev,
                availableSubjects: response.preview.speakers,
              }));
              console.log(`Upload success for ${file.name}:`, response);
            }
            setIsLoading(false);
          } else {
            // The server returned a 400 or 500 level error
            setSuccess(false);

            // Look for your specific API error payload first
            if (response.error) {
              if (response.error.transcript) {
                setError(response.error.transcript[0]);
              } else {
                setError(response.error);
              }
            } else {
              // Fallback if the JSON doesn't contain the expected error structure
              setError(`Upload failed: ${xhr.statusText}`);
            }
            setIsLoading(false);
          }
        } catch (e) {
          // If the server crashes and sends back HTML instead of JSON
          setSuccess(false);
          setError(`Server error: ${xhr.status}`);
        }
      };

      xhr.open(
        "POST",
        `${brand.api_base_url}/conversation_insight/validate`,
        true,
      );

      xhr.setRequestHeader("Authorization", `Token ${authToken}`);
      xhr.send(formData);
    });
  };

  const pollFeedbackStatus = async (insightId) => {
    try {
      const response = await callAPI(
        `conversation_insight/status/${insightId}`,
        {
          token: authToken,
        },
      );
      if (response && response.job_status === "Completed") {
        console.log("Feedback generation complete!", response);
        setIsGenerating(false);

        setTimeout(() => {
          navigate(
            `/dashboard/feedback/${insightId}?conversation_insight_complete`,
          );
        }, 200);
      } else {
        console.log(`Still processing... Status: ${response?.job_status}`);
        setTranscriptData((prev) => ({
          ...prev,
          generationProgress: {
            job_status: response.job_status,
            job_status_steps: response.job_status_steps,
          },
        }));
        setTimeout(() => {
          pollFeedbackStatus(insightId);
        }, 1000);
      }
    } catch (err) {
      console.error("Failed to poll status:", err);
      setMessage({
        type: "error",
        msg: err.message || err.detail || "Unable to generate feedback",
      });
      setModal({ type: "", show: false, title: "" });
      setIsGenerating(false);
    }
  };

  const startFeedbackGeneration = async () => {
    setModal({
      type: "",
      show: false,
      title: "",
    });

    setIsGenerating(true);

    // 1. Build the FormData payload
    const formData = new FormData();
    formData.append("transcript", transcriptData.file);
    formData.append("name", transcriptData.name);
    formData.append("subject", transcriptData.subject);

    if (transcriptData.training_program_id) {
      formData.append("trainingprogram_id", transcriptData.training_program_id);
    }

    // 2. Call your upgraded API hook
    await callAPI("conversation_insight/start", {
      method: "POST",
      body: formData, // Passing FormData instead of a standard object
      token: authToken,
    })
      .then((data) => {
        console.log("conversation insight success:", data);
        const newInsightId = data.conversation_insight_id;

        if (newInsightId) {
          // Kick off the polling loop
          setTranscriptData((prev) => ({
            ...prev,
            id: newInsightId,
          }));
          pollFeedbackStatus(newInsightId);
        } else {
          throw new Error("API did not return an ID to poll.");
        }
      })
      .catch((err) => {
        console.error("Failed to generate feedback:", err);
        setModal({ type: "", show: false, title: "" }); // close modal on error
        setMessage({
          type: "error",
          msg: err.message || err.detail || "Unable to generate feedback",
        });
      });
  };

  const trainingProgramOptions = useMemo(() => {
    let trainingPrograms = [];
    if (allTrainingPrograms) {
      Object.keys(allTrainingPrograms).forEach((i) => {
        trainingPrograms.push({
          label: allTrainingPrograms[i].name,
          value: allTrainingPrograms[i].id,
        });
      });
    }
    return trainingPrograms;
  }, [allTrainingPrograms]);

  console.log({ transcriptData });

  return (
    <ConvoInsightsContainer>
      {generating && (
        <StackedProgress apiProgress={transcriptData.generationProgress} />
      )}
      <PageTitle text="Conversation Insights">
        Simply upload a transcript from a previous meeting or conversation and
        receive instant feedback and coaching.
      </PageTitle>

      {message.type !== "" && message.msg !== "" && (
        <Alert severity={message.type} sx={{ mb: 4, mt: 4 }}>
          {message.msg}
        </Alert>
      )}

      <div className="upload-container">
        <div className="heading-container">
          <h2>
            Upload Previous Conversation Transcript{" "}
            <span>
              Upload documents, presentations, and other materials for your
              training program.
            </span>
          </h2>
          <div className="category-select">
            <Form action="#">
              {({
                register,
                formState: { errors },
                watch,
                control,
                setValue,
              }) => (
                <>
                  <ProgramWatcher
                    setValue={setValue}
                    options={trainingProgramOptions}
                    setTranscriptData={setTranscriptData}
                    currentProgramId={transcriptData.training_program_id}
                  />
                  <Input
                    id="program_filter"
                    label="Category"
                    showLabel
                    type="select"
                    placeHolder="Select Category"
                    register={register("program_filter")}
                    control={control}
                    options={trainingProgramOptions}
                    onOptionSelect={(option) => {
                      setTranscriptData((prev) => ({
                        ...prev,
                        training_program_id: option,
                      }));
                    }}
                    error={errors.program_filter?.message}
                    dark
                  />
                </>
              )}
            </Form>
          </div>
        </div>
        <div className={`form-container ${isLoading ? "uploading" : ""}`}>
          {isLoading && (
            <div className="uploading-loading">
              <CircularProgress enableTrackSlot />
              <span>Validating transcript...</span>
            </div>
          )}
          <Form action="#">
            {({
              register,
              formState: { errors },
              watch,
              control,
              setValue,
              getValues,
            }) => (
              <>
                <TitleWatcher
                  watch={watch}
                  setTranscriptData={setTranscriptData}
                />
                <Input
                  type="text"
                  id="conversation_insight_name"
                  register={register("conversation_insight_name")}
                  label="Title"
                  showLabel
                  helpText="This will be shown in the performance spotlight table"
                  placeHolder="Enter a title for your transcript review, defaults to filename"
                  dark
                />
                <DragDropFileUpload
                  showProgress
                  onFileRemove={() => {
                    // 1. Reset the transcript data
                    setTranscriptData((prev) => ({
                      ...prev,
                      file: "",
                      name: "",
                      availableSubjects: [],
                    }));
                    // 2. Disable the submit button
                    setIsValidFile(false);
                    // 3. Clear the input field title if you want
                    setValue("conversation_insight_name", "", {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  onFilesSelected={(
                    files,
                    setUploadProgress,
                    setError,
                    setSuccess,
                  ) => {
                    // 1. Check if a name has already been entered
                    const currentTitle = getValues("conversation_insight_name");

                    // 2. If it's empty, set the input value to the first uploaded file's name
                    if (!currentTitle || currentTitle.trim() === "") {
                      if (files.length > 0) {
                        // We use files[0].name and strip the extension for a cleaner title
                        const fileNameWithoutExt = files[0].name.replace(
                          /\.[^/.]+$/,
                          "",
                        );
                        setValue(
                          "conversation_insight_name",
                          fileNameWithoutExt,
                          {
                            shouldValidate: true,
                            shouldDirty: true,
                          },
                        );
                      }
                    }

                    // 3. Call your original upload logic
                    handleFilesSelected(
                      files,
                      setUploadProgress,
                      setError,
                      setSuccess,
                    );
                  }}
                  maxFiles={1}
                  accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/plain"
                  maxSize={50 * 1024 * 1024}
                  uploadTypeText="TXT, DOCX, DOC, JSON, HTML, CSV (2MB MAX)"
                />
              </>
            )}
          </Form>
        </div>
      </div>
      <div className="actions">
        <DefaultButton
          onClick={() =>
            setModal({
              type: "speaker_select",
              show: true,
              title: "Identify yourself",
            })
          }
          wideOnMobile
          buttonSize="lg"
          disabled={!isValidFile}
        >
          Generate Feedback
        </DefaultButton>
      </div>
      <div className="what-next">
        <h2>What happens next?</h2>
        <ul>
          {whatNext.map((item, i) => (
            <li key={i}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z"
                  fill="#01ECFF"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M17.0964 7.39004L9.93641 14.3L8.03641 12.27C7.68641 11.94 7.13641 11.92 6.73641 12.2C6.34641 12.49 6.23641 13 6.47641 13.41L8.72641 17.07C8.94641 17.41 9.32641 17.62 9.75641 17.62C10.1664 17.62 10.5564 17.41 10.7764 17.07C11.1364 16.6 18.0064 8.41004 18.0064 8.41004C18.9064 7.49004 17.8164 6.68004 17.0964 7.38004V7.39004Z"
                  fill="#211F45"
                />
              </svg>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <Modal
        id="conversation-insights-modal"
        title={modal.title}
        open={modal.show}
        onClose={() => {
          setModal({ type: "", show: false, title: "" });
        }}
        showIcon
        showClose={false}
        icon={<PeopleOutlineIcon />}
      >
        {modal.type === "speaker_select" && (
          <>
            <p>
              We detected {transcriptData.availableSubjects.length} speakers in
              the transcript, please confirm which one you are.
            </p>
            <div className="available-subjects">
              {transcriptData.availableSubjects.map((speaker, i) => (
                <button
                  className={`speaker ${speaker === transcriptData.subject ? "active" : ""}`}
                  onClick={() => {
                    setTranscriptData((prev) => ({
                      ...prev,
                      subject: speaker,
                    }));
                  }}
                >
                  <PersonOutlineIcon
                    style={{
                      color:
                        speaker === transcriptData.subject ? "#962EFF" : "",
                    }}
                  />{" "}
                  {speaker}
                </button>
              ))}
            </div>
            <div className="actions">
              {transcriptData.subject && (
                <DefaultButton
                  onClick={() => startFeedbackGeneration()}
                  wideOnMobile
                  buttonSize="lg"
                  className="action-button"
                >
                  Start Generating
                </DefaultButton>
              )}
              <button
                className="cancel"
                onClick={() => setModal({ type: "", show: false, title: "" })}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </Modal>
    </ConvoInsightsContainer>
  );
};

export default ConversationInsights;
